import { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { usePersistedBuilding } from '../hooks/usePersistedBuilding';
import { useGetBuildingsQuery } from '../redux/api/location';

interface BuildingContextValue {
	buildingId: string | null;
	buildingName: string | null;
	buildings: { id: string; name: string }[];
	isLoading: boolean;
	isResolved: boolean;
	setBuildingId: (id: string) => void;
	clearBuildingId: () => void;
}

const BuildingContext = createContext<BuildingContextValue | null>(null);

export function BuildingProvider({ children }: { children: ReactNode }) {
	const { buildingId, setBuildingId, clearBuildingId } = usePersistedBuilding();
	const { data: buildings = [], isLoading } = useGetBuildingsQuery();

	useEffect(() => {
		if (!isLoading && buildings.length === 1 && buildingId !== buildings[0].id) {
			setBuildingId(buildings[0].id);
		}
	}, [isLoading, buildings, buildingId, setBuildingId]);

	useEffect(() => {
		if (!isLoading && buildings.length > 0 && buildingId) {
			const stillValid = buildings.some((b) => b.id === buildingId);
			if (!stillValid) {
				clearBuildingId();
			}
		}
	}, [isLoading, buildings, buildingId, clearBuildingId]);

	const buildingName = useMemo(() => {
		if (!buildingId) return null;
		return buildings.find((b) => b.id === buildingId)?.name ?? null;
	}, [buildingId, buildings]);

	const isResolved = !isLoading && buildingId !== null;

	const value = useMemo<BuildingContextValue>(
		() => ({
			buildingId,
			buildingName,
			buildings,
			isLoading,
			isResolved,
			setBuildingId,
			clearBuildingId,
		}),
		[buildingId, buildingName, buildings, isLoading, isResolved, setBuildingId, clearBuildingId],
	);

	return <BuildingContext.Provider value={value}>{children}</BuildingContext.Provider>;
}

export function useBuildingContext(): BuildingContextValue {
	const ctx = useContext(BuildingContext);
	if (!ctx) {
		throw new Error('useBuildingContext must be used within a BuildingProvider');
	}
	return ctx;
}
